/**
 * Tropical → sidereal. The only such place.
 *
 * The ayanāṁśa is the offset between the tropical and sidereal zodiacs. Systems
 * differ by up to ~1.5°, which moves planets across pada and sometimes rāśi
 * boundaries, so the system is a first-class setting and is recorded with every
 * saved chart.
 *
 * Supported: Lahiri / Chitrapakṣa (official, Calendar Reform Committee 1955),
 * Krishnamurti (KP), B. V. Raman, Śrī Yukteśvar, Fagan–Bradley and true Citrā
 * (Spica fixed at 180°00′).
 *
 * ⚠ true Revatī and true Puṣya are **not** shipped — see the note below the
 * tables. They were written, measured, found to be degrees wrong, and pulled.
 *
 * The precession-based systems are computed from precession, not from a linear
 * rate: a linear 50.29″/yr is already ~0.1° off for a 1900 birth.
 *
 * © Manish Jagdish Thatte
 */

import {
  Body, DefineStar, Ecliptic, GeoVector, InverseRotation, RotateVector,
  Rotation_EQJ_ECT, SphereFromVector, Spherical, Vector, VectorFromSphere
} from 'astronomy-engine';
import { J2000 } from './julian.js';
import { timeFromTT } from './ephemeris.js';

const norm360 = (d) => ((d % 360) + 360) % 360;
const D2R = Math.PI / 180;

/**
 * ── How an anchored ayanāṁśa is computed here ───────────────────────────────
 *
 * A sidereal zodiac's zero point is a **fixed direction in space**. The
 * ayanāṁśa at time t is simply that direction's tropical longitude at t.
 *
 * So instead of a precession *series*, we do the exact thing: take the zero
 * point's longitude at the system's defining epoch, turn it into a fixed J2000
 * equatorial vector once, and read its longitude in the true ecliptic of date
 * whenever asked. Precession and nutation come from astronomy-engine's IAU
 * rotations, which are already the models the rest of the layer uses.
 *
 * ◆ This removes the "linear 50.29″/yr" error the plan warns about **and** the
 * risk of a hand-rolled precession polynomial, in about ten lines.
 */

/**
 * Anchored systems: the ayanāṁśa value at a stated epoch.
 *
 * ⚠ `verified` is the important column. `true` means the constant has been
 * checked against a value published by someone other than us; `false` means it
 * is carried here to be checked and **must not be presented to a user as
 * authoritative** until it is. See tools/verify-ayanamsa.mjs.
 */
const ANCHORED = {
  lahiri: {
    label: 'Lahiri (Chitrapakṣa)',
    epochJD: 2435553.5,      // 1956-03-21 00:00 TT
    valueDeg: 23.250182,     // 23°15′00.66″
    verified: true,
    source:
      'Indian Calendar Reform Committee (1955), as implemented by Swiss Ephemeris ' +
      'SE_SIDM_LAHIRI. Corroborated here: reproduces the independently published ' +
      '2000-01-01 value 23°51′11″ to within 0.3″, and 1950-01-01 23°09′ to within 28″.'
  },
  kp: {
    label: 'Krishnamurti (KP)',
    epochJD: 2415020.5,      // 1900-01-01 00:00 TT
    valueDeg: 22.3788222,    // 22°22′43.76″
    verified: false,
    source:
      'K. S. Krishnamurti\'s stated 1900 value, as commonly reproduced. ⚠ NOT yet ' +
      'checked against a primary KP source. Relationship test only: KP should sit ' +
      '~5–6′ below Lahiri, which it does.'
  },
  raman: {
    label: 'B. V. Raman',
    epochJD: 2415020.5,      // 1900-01-01 00:00 TT
    valueDeg: 21.0,          // 21°00′00″
    verified: false,
    source:
      'B. V. Raman\'s stated 1900 value of exactly 21°. ⚠ The roundness is itself a ' +
      'reason to check it against Raman\'s own tables before publishing.'
  },
  yukteshwar: {
    label: 'Śrī Yukteśvar',
    epochJD: 2415020.5,      // 1900-01-01 00:00 TT
    valueDeg: 20.91,         // 20°54′36″
    verified: false,
    source:
      'Śrī Yukteśvar, *The Holy Science* (1894), as commonly tabulated. ⚠ The ' +
      'epoch attribution is uncertain and this value is the least corroborated ' +
      'of the set.'
  },
  fagan: {
    label: 'Fagan–Bradley',
    epochJD: 2433282.5,      // 1950-01-01 00:00 TT
    valueDeg: 24.042044,     // 24°02′31.36″ — the synetic vernal point
    verified: false,
    source:
      'Fagan & Bradley\'s synetic vernal point, 24°02′31.36″ at 1950.0. ⚠ Widely ' +
      'published but not yet checked against a primary source. Sanity holds: it ' +
      'sits ~53′ above Lahiri, which is the known offset.'
  }
};

/**
 * Star-anchored systems: no epoch constant at all. The yogatārā's apparent
 * longitude is computed and the defined value subtracted.
 *
 * ◆ These are the honest ones — nothing is remembered except the star and the
 * definition — and [A1](common/plans/A1-validation.md) uses true Citrā as an
 * independent check on Lahiri's anchor.
 *
 * ⚠ Positions are Hipparcos J2000 with linear proper motion applied. Proper
 * motion matters: Spica moves the true-Citrā ayanāṁśa by about 4.7″ per century
 * relative to a fixed point, which is visible in the Lahiri comparison.
 *
 * ⚠ Only true Citrā is here. See the note below for why the other two are not.
 */
const STARRED = {
  trueCitra: {
    label: 'true Citrā (Spica at 180°)',
    fixedLon: 180,
    star: { name: 'Spica (α Vir, HIP 65474)', raDeg: 201.2982473, decDeg: -11.1613028, pmRA: -42.50, pmDec: -31.73, ly: 262 },
    verified: true,
    source:
      'Hipparcos α Vir (Spica), with linear proper motion. Corroborated by ' +
      'construction rather than by a table: it tracks Lahiri to 57–62″ across ' +
      '1900–2100, and the residual GROWS by ~5″/century, which is Spica\'s own ' +
      'proper motion against Lahiri\'s fixed point. A wrong star would have given ' +
      'a large constant offset; wrong proper motions would have given divergence. ' +
      'Neither is present. ⚠ The pm values themselves still want a catalogue check.'
  }
};

/**
 * ⚠ **true Revatī and true Puṣya are NOT shipped, and that is deliberate.**
 *
 * They were implemented with ζ Piscium and δ Cancri positions taken from memory
 * rather than from a catalogue, and the true-Citrā cross-check caught both:
 *
 *     trueCitra   2000-01-01   23.83604°   Lahiri − this =  0.0171°   ✓ sound
 *     trueRevati  2000-01-01   20.04134°   Lahiri − this =  3.8118°   ✗
 *     truePushya  2000-01-01   22.72320°   Lahiri − this =  1.1299°   ✗
 *
 * All three should sit within roughly a degree of Lahiri; 3.8° is not a
 * refinement, it is a different zodiac. A user who picked "true Revatī" would
 * have got a coherent, confident, entirely wrong chart — every graha nearly four
 * degrees out, which is more than a nakṣatra pada.
 *
 * ◆ They come back when ζ Psc and δ Cnc have positions from a citable catalogue,
 * checked the same way. Until then the honest set is the five anchored systems
 * plus true Citrā. This is the plan's own rule about remembered constants,
 * applied to code I had already written.
 */

/** ◆ The default, and what every Indian pañcāṅga means by "the" ayanāṁśa. */
export const DEFAULT_AYANAMSA = 'lahiri';

const fixedPointCache = new Map();

/**
 * Turn "value V at epoch E" into the fixed J2000 direction it names, once.
 */
function fixedPointEQJ(id) {
  let v = fixedPointCache.get(id);
  if (v) return v;
  const { epochJD, valueDeg } = ANCHORED[id];
  const t0 = timeFromTT(epochJD);
  const inEclOfEpoch = VectorFromSphere(new Spherical(0, valueDeg, 1), t0);
  v = RotateVector(InverseRotation(Rotation_EQJ_ECT(t0)), inEclOfEpoch);
  fixedPointCache.set(id, v);
  return v;
}

function anchoredValue(id, jdTT) {
  const p = fixedPointEQJ(id);
  const t = timeFromTT(jdTT);
  const s = SphereFromVector(RotateVector(Rotation_EQJ_ECT(t), new Vector(p.x, p.y, p.z, t)));
  return norm360(s.lon);
}

// astronomy-engine gives eight user-definable star slots; we need at most one at
// a time, and redefining is cheap.
function starredValue(id, jdTT) {
  const { star, fixedLon } = STARRED[id];
  const years = (jdTT - J2000) / 365.25;
  // Linear proper motion, mas/yr → degrees. pmRA is μα·cos δ, so undo the cos δ
  // before adding it to right ascension.
  const dec = star.decDeg + (star.pmDec * years) / 3.6e6;
  const ra = star.raDeg + (star.pmRA * years) / 3.6e6 / Math.cos(star.decDeg * D2R);

  DefineStar(Body.Star1, ra / 15, dec, star.ly);
  const apparent = Ecliptic(GeoVector(Body.Star1, timeFromTT(jdTT), true)).elon;
  return norm360(apparent - fixedLon);
}

/**
 * The ayanāṁśa in degrees at JD_TT, for one system.
 *
 * ⚠ An unknown id **throws**. It must never quietly fall back to Lahiri: a
 * chart silently computed in a different zodiac than the one it is labelled
 * with is the single most expensive mistake this module can make.
 */
export function ayanamsaValue(jdTT, system = DEFAULT_AYANAMSA) {
  if (!Number.isFinite(jdTT)) throw new TypeError('ayanamsaValue: jdTT must be finite');
  const id = String(system);
  if (id in ANCHORED) return anchoredValue(id, jdTT);
  if (id in STARRED) return starredValue(id, jdTT);
  throw new RangeError(
    `unknown ayanāṁśa "${system}" — expected one of ${Object.keys({ ...ANCHORED, ...STARRED }).join(', ')}`
  );
}

/** Tropical longitude → sidereal. The one place this subtraction happens. */
export function toSidereal(tropicalLon, jdTT, system = DEFAULT_AYANAMSA) {
  return norm360(tropicalLon - ayanamsaValue(jdTT, system));
}

/** Sidereal longitude → tropical. Exact inverse of `toSidereal`. */
export function toTropical(siderealLon, jdTT, system = DEFAULT_AYANAMSA) {
  return norm360(siderealLon + ayanamsaValue(jdTT, system));
}

/** Every supported system with its provenance, for the UI and for tools. */
export function ayanamsaSystems() {
  const out = [];
  for (const [id, a] of Object.entries(ANCHORED)) {
    out.push({
      id, label: a.label, kind: 'anchored', verified: a.verified, source: a.source,
      epochJD: a.epochJD, valueDeg: a.valueDeg
    });
  }
  for (const [id, s] of Object.entries(STARRED)) {
    out.push({
      id, label: s.label, kind: 'star', verified: s.verified, source: s.source,
      star: s.star.name, fixedLon: s.fixedLon
    });
  }
  return out;
}

/** The ids whose constants have been checked against an outside publication. */
export function verifiedSystems() {
  return ayanamsaSystems().filter((s) => s.verified).map((s) => s.id);
}
